import React, { useCallback } from "react";
import useSWR, { mutate } from "swr";
export function useServerState({ key, fetcher, manual = false, enabled = true, onSuccess, onError, config, }) {
    // Create a stable key for SWR
    const swrKey = React.useMemo(() => {
        if (!enabled || manual)
            return null;
        return JSON.stringify(key);
    }, [key, enabled, manual]);
    // SWR fetcher wrapper that handles our fetcher format
    const swrFetcher = useCallback(async (keyString) => {
        const parsedKey = JSON.parse(keyString);
        const controller = new AbortController();
        return fetcher(undefined, controller.signal);
    }, [fetcher]);
    // Use SWR for data fetching
    const { data, error, isLoading, mutate: swrMutate, } = useSWR(swrKey, swrFetcher, {
        revalidateOnFocus: false,
        revalidateOnReconnect: true,
        shouldRetryOnError: true,
        ...config,
        onSuccess: (data, key, swrConfig) => {
            onSuccess?.(data);
            config?.onSuccess?.(data, key, swrConfig);
        },
        onError: (error, key, swrConfig) => {
            const err = error instanceof Error ? error : new Error(String(error));
            onError?.(err);
            config?.onError?.(error, key, swrConfig);
        },
    });
    // Manual trigger function
    const trigger = useCallback(async (triggerData) => {
        try {
            const controller = new AbortController();
            const result = await fetcher(triggerData, controller.signal);
            // Update SWR cache with the new data
            const cacheKey = JSON.stringify(key);
            mutate(cacheKey, result, false);
            onSuccess?.(result);
            return result;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            onError?.(error);
            throw error;
        }
    }, [fetcher, key, onSuccess, onError]);
    // Refetch function using SWR's mutate
    const refetch = useCallback(async () => {
        const result = await swrMutate();
        if (result === undefined) {
            throw new Error("Failed to refetch data");
        }
        return result;
    }, [swrMutate]);
    return {
        data: data ?? null,
        error: error ? (error instanceof Error ? error : new Error(String(error))) : null,
        isLoading: isLoading ?? false,
        trigger,
        refetch,
    };
}
